import { isValidElement, type ButtonHTMLAttributes, type ReactNode } from "react";

export type ButtonVariant = "primary" | "secondary" | "muted" | "danger" | "whatsapp" | "blocked";

const BASE_CLASSES =
  "font-semibold uppercase rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: "bg-primary-600 text-white hover:bg-primary-700 disabled:hover:bg-primary-600",
  secondary: "bg-white border border-primary-600 text-primary-600 hover:bg-primary-50 disabled:hover:bg-white",
  muted: "bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:hover:bg-gray-200",
  danger: "bg-red-600 text-white hover:bg-red-700 disabled:hover:bg-red-600",
  whatsapp: "bg-green-500 text-white hover:bg-green-700 disabled:hover:bg-green-500",
  blocked: "bg-gray-200 text-gray-500 cursor-not-allowed",
};

// Also for a Link that should look like a button. Size and layout stay with the caller.
export const buttonClasses = (variant: ButtonVariant = "primary", className = "") =>
  `${BASE_CLASSES} ${VARIANT_CLASSES[variant]} ${className}`.trim();

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  // Shown before the label; the spinner takes its place while loading
  icon?: ReactNode;
  // The label stays the same while loading, so the button keeps its width
  isLoading?: boolean;
};

// Sized in em, like react-icons, so it takes exactly the icon's place
export const ButtonSpinner = ({ className = "" }: { className?: string }) => (
  <span
    aria-hidden="true"
    className={`inline-block h-[1em] w-[1em] shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent ${className}`.trim()}
  />
);

const Button = ({ variant = "primary", className = "", icon, isLoading, disabled, children, ...props }: ButtonProps) => {
  const hasLeading = isLoading || icon !== undefined;
  // The icon's own size classes (text-xs, for example) give the spinner the same size
  const iconClassName = isValidElement<{ className?: string }>(icon) ? icon.props.className : undefined;
  // Callers that already lay the button out as flex keep their own alignment and gap
  const layout = hasLeading && !/(^|\s)(inline-)?flex(\s|$)/.test(className)
    ? "inline-flex items-center justify-center gap-2"
    : "";

  return (
    <button
      className={buttonClasses(variant, `${layout} ${className}`.trim())}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {isLoading ? <ButtonSpinner className={iconClassName} /> : icon}
      {children}
    </button>
  );
};

export default Button;
